import { NotificationType } from '@api/READ_ONLY/notifications_api/Api';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle } from 'react-icons/fi';
import { DeleteShiftNotificationConflictType, UpdateShiftConflictNotificationType } from '../../types/notifications';
import { LIST_ITEM_ANIMATIONS } from '../consts/motionAnimations';
import { inDisplayZone } from '@app/utils/dates/displayTimezone';

type ShiftConflictWidgetNotificationCardProps = {
  notificationDetails: DeleteShiftNotificationConflictType | UpdateShiftConflictNotificationType;
  onClick: (id: number) => void;
};

/** The widget's compact form of {@link ShiftConflictNotificationCard}: how many
 * visits were left unassigned, and the first of them. The rest is the full
 * card's job - and like there, the visits come from `metadata` rather than from
 * the body, which carries the same list as raw text. */
const ShiftConflictWidgetNotificationCard = ({
  notificationDetails,
  onClick,
}: ShiftConflictWidgetNotificationCardProps) => {
  const { t } = useTranslation();
  const timeAgo = (iso: string) => dayjs(iso).fromNow();

  const visits = notificationDetails.metadata ?? [];
  const first = visits[0];

  const lead =
    notificationDetails.type === NotificationType.DeleteShiftConflict
      ? t('notifications-card-shift-conflict-deleted')
      : t('notifications-card-shift-conflict-updated');

  return (
    <motion.li key={notificationDetails.notificationId} className="overflow-hidden" {...LIST_ITEM_ANIMATIONS}>
      <div className={`group flex flex-col px-4 py-3 hover:bg-slate-50 cursor-pointer`}>
        <div
          onClick={() => onClick(notificationDetails.notificationId)}
          className="flex w-full items-start gap-3 text-left"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <FiAlertTriangle size={18} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-700 line-clamp-2">{notificationDetails.title}</h3>

                {visits.length > 0 && (
                  <Tag bordered={false} color="warning">
                    <span className="font-light">
                      {t('notifications-shift-conflict-visit-count', { count: visits.length })}
                    </span>
                  </Tag>
                )}
              </div>

              <span className="ml-2 shrink-0 text-xs text-slate-400">{timeAgo(notificationDetails.createdAt)}</span>
            </div>

            <p className="mt-0.5 text-xs text-slate-600">{lead}</p>

            {first && (
              <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">
                {[first.clientFirstName, first.clientLastName].filter(Boolean).join(' ')}
                {first.visitStartTime && ` · ${inDisplayZone(first.visitStartTime).format('DD MMM, HH:mm')}`}
              </p>
            )}
          </div>

          {!notificationDetails.isRead && <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-amber-500" />}
        </div>
      </div>
    </motion.li>
  );
};

export default ShiftConflictWidgetNotificationCard;
